import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Archive, Car, ChevronDown, CircleDollarSign, ClipboardList, Eye, FileCheck2, Package, Wrench } from 'lucide-react';
import { bearerKind, bearerLabel, eventPresentation, formatDate, formatMoney, isSupplierItem, methodLabel, settlement } from './operationsLogModel.js';
import './OperationsCards.css';

const ICONS = { archive: Archive, car: Car, payment: CircleDollarSign, approval: FileCheck2, package: Package, wrench: Wrench, record: ClipboardList };
const SOURCE_LABELS = {
    visit_received: 'سجل الاستقبال', service_item: 'بند خدمة', part_item: 'بند قطعة',
    supplier_purchase: 'شراء مورد', customer_payment: 'دفعة الزيارة',
    visit_finalized: 'اعتماد الزيارة', vehicle_delivered: 'التسليم والأرشفة',
};

export const Money = ({ value }) => <span className={`operations-money${value === null || value === undefined ? ' operations-money-unavailable' : ''}`}>
    <bdi dir="ltr">{formatMoney(value)}</bdi>{value !== null && value !== undefined && <small>ر.س</small>}
</span>;

const Badge = ({ children, tone = '' }) => <span className={`operations-badge ${tone ? `operations-badge-${tone}` : ''}`}>{children}</span>;

const VehicleContext = ({ vehicle, visit, id, icon = 'wrench', title }) => {
    const Icon = ICONS[icon];
    return <div className="operations-card-identity">
        <span className="operations-card-icon" aria-hidden="true"><Icon size={27} strokeWidth={1.9} /></span>
        <div className="operations-card-identity-copy">
            {title && <p className="operations-card-kind">{title}</p>}
            <h2>{vehicle.customerName || 'العميل غير مسجل'}</h2>
            <p className="operations-card-vehicle-name" data-testid={`operations-card-vehicle-name-${id}`}>
                {vehicle.name || 'اسم المركبة غير مسجل'}{vehicle.year && <><span aria-hidden="true"> · </span><bdi>{vehicle.year}</bdi></>}
            </p>
            <p className="operations-card-reference">
                {vehicle.plateNumber && <bdi>{vehicle.plateNumber}</bdi>}
                {vehicle.fileNumber && <span>ملف <bdi>{vehicle.fileNumber}</bdi></span>}
                {visit.number && <span>زيارة <bdi>{visit.number}</bdi></span>}
            </p>
        </div>
    </div>;
};

const VehicleLink = ({ vehicleId, canOpenVehicle, testId }) => canOpenVehicle && vehicleId ? <Link
    className="operations-vehicle-link" to={`/vehicle/${encodeURIComponent(vehicleId)}`} data-testid={testId}
><Eye size={17} aria-hidden="true" />فتح ملف المركبة</Link> : null;

const ExpandButton = ({ expanded, onClick, contentId, testId }) => <button
    type="button" className="operations-card-expand" onClick={onClick} aria-expanded={expanded} aria-controls={contentId} data-testid={testId}
><ChevronDown size={22} className={expanded ? 'is-expanded' : ''} aria-hidden="true" />{expanded ? 'إخفاء التفاصيل' : 'عرض التفاصيل'}</button>;

const Field = ({ label, children }) => <div><dt>{label}</dt><dd>{children}</dd></div>;

const Metric = ({ label, value, testId }) => <div className="operations-metric" data-testid={testId}>
    <dt>{label}</dt><dd><Money value={value} /></dd>
</div>;

const itemCountLabel = (count, singular, dual, plural) => count === 1 ? singular : count === 2 ? dual : `${count} ${count >= 3 && count <= 10 ? plural : singular}`;

const summaryBearer = (items) => {
    const bearers = new Set(items.map(bearerKind));
    if (bearers.size === 0 || bearers.has('unknown')) return 'غير مكتملة';
    return bearers.size > 1 ? 'موزع' : bearers.has('customer') ? 'العميل' : 'الورشة';
};

const SummaryStatus = ({ summary }) => {
    const states = [];
    if (summary.verified && summary.financialState === 'finalized') {
        states.push(<Badge key="state" tone="green">معتمدة</Badge>);
        if (summary.remaining !== null && summary.collectedTotal !== null && summary.advance !== null) {
            if (summary.advance > 0) states.push(<Badge key="payment" tone="teal">رصيد لصالح العميل</Badge>);
            else if (summary.remaining > 0 && summary.collectedTotal > 0) states.push(<Badge key="payment" tone="amber">جزئية</Badge>);
            else if (summary.remaining > 0) states.push(<Badge key="payment" tone="amber">غير مسددة</Badge>);
            else if (summary.remaining === 0) states.push(<Badge key="payment" tone="green">{summary.amount === 0 ? 'لا ذمة' : 'مسددة'}</Badge>);
        }
    } else if (summary.verified && summary.financialState === 'open') {
        states.push(<Badge key="state">تقديرية</Badge>);
    } else states.push(<Badge key="state">المبالغ غير متاحة</Badge>);
    return states;
};

const ItemRows = ({ summary, items }) => items.length > 0 && <section className="operations-items-section" aria-label="بنود الزيارة">
    <h3>البنود</h3>
    <div className="operations-items" data-testid={`operations-summary-items-${summary.visitId}`}>
        <div className="operations-items-heading" aria-hidden="true"><span>البند</span><span>الكمية</span><span>السعر</span><span>القيمة</span></div>
        {items.map((item) => <div className="operations-item" key={item.id} data-testid={`operations-summary-item-${summary.visitId}-${item.id}`}>
            <div className="operations-item-name">
                <strong>{item.name || 'بند غير مسمى'}</strong>
                {bearerKind(item) !== 'customer' && <small>{bearerKind(item) === 'workshop' ? 'على الورشة' : 'التحمل غير محدد'}</small>}
            </div>
            <div className="operations-item-number"><span>الكمية</span><bdi>{item.quantity === null ? 'غير متاح' : item.quantity}</bdi></div>
            <div className="operations-item-number"><span>السعر</span><Money value={item.unitPrice} /></div>
            <div className="operations-item-number"><span>القيمة</span><Money value={item.lineTotal} /></div>
        </div>)}
    </div>
</section>;

const SupplierRows = ({ summary, items }) => items.length > 0 && <section className="operations-suppliers-section" aria-label="مشتريات الموردين المرتبطة">
    <h3>مشتريات مرتبطة</h3>
    {items.map((item) => <div className="operations-linked-purchase" key={item.id} data-testid={`operations-summary-supplier-${summary.visitId}-${item.id}`}>
        <div className="operations-linked-purchase-header">
            <Package size={23} aria-hidden="true" />
            <div><strong>{item.name || 'شراء مورد'}</strong><span>{item.supplierName || 'المورد غير مسجل'}</span></div>
            <Money value={item.lineTotal} />
        </div>
        <dl className="operations-purchase-facts">
            <Field label="الكمية">{item.quantity === null ? 'غير متاح' : item.quantity}</Field>
            <Field label="سعر الوحدة"><Money value={item.unitPrice} /></Field>
            <Field label="التحمل">{bearerLabel(item)}</Field>
            <Field label="السداد">{settlement(item.supplierSettlementStatus).label}</Field>
            {item.supplierPaymentMethod && settlement(item.supplierSettlementStatus).settled && <Field label="الوسيلة">{methodLabel(item.supplierPaymentMethod)}</Field>}
        </dl>
    </div>)}
    <p className="operations-small-note">سداد المورد مستقل عن تحصيل العميل. المشتريات المحمّلة على العميل محسوبة ضمن الإجمالي.</p>
</section>;

export const VisitSummaryCard = ({ summary, canOpenVehicle }) => {
    const [expanded, setExpanded] = useState(false);
    const services = summary.items.filter((item) => item.itemType === 'service');
    const parts = summary.items.filter((item) => item.itemType === 'part');
    const suppliers = summary.items.filter(isSupplierItem);
    const ordinaryItems = summary.items.filter((item) => !isSupplierItem(item));
    const descriptor = [services.length ? itemCountLabel(services.length, 'خدمة', 'خدمتان', 'خدمات') : '', parts.length ? itemCountLabel(parts.length, 'قطعة', 'قطعتان', 'قطع') : '', suppliers.length ? `مشتريات: ${suppliers.length}` : ''].filter(Boolean).join(' · ');
    const contentId = `visit-details-${summary.visitId}`;
    const outstandingLabel = summary.financialState === 'open' ? 'المتبقي بعد الاعتماد' : 'المتبقي لصالح الورشة';
    const showAdvance = summary.advance !== null && summary.advance > 0;
    return <article className="operations-agreed-card operations-tone-customer" data-testid={`operations-visit-summary-${summary.visitId}`} data-card="visit-summary" data-tone="customer">
        <div className="operations-card-top">
            <VehicleContext vehicle={summary.vehicle} visit={summary.visit} id={summary.id} title="ملخص الزيارة" />
            <div className="operations-card-main-total" data-testid={`operations-summary-metric-${summary.visitId}-amount`}>
                <span>الإجمالي</span>
                <Money value={summary.amount} />
            </div>
        </div>
        <div className="operations-card-badges">
            <Badge tone="blue">{descriptor || 'دون بنود'}</Badge><SummaryStatus summary={summary} />
        </div>
        <dl className="operations-metrics">
            <Metric label="المحصل" value={summary.collectedTotal} testId={`operations-summary-metric-${summary.visitId}-collectedTotal`} />
            <Metric label={outstandingLabel} value={summary.remaining} testId={`operations-summary-metric-${summary.visitId}-remaining`} />
            {showAdvance && <Metric label="مقدم لصالح العميل" value={summary.advance} testId={`operations-summary-metric-${summary.visitId}-advance`} />}
        </dl>
        {summary.financialState === 'open' && summary.verified && <p className="operations-small-note">يظهر المستحق بعد الاعتماد.</p>}
        {!summary.verified && <p className="operations-card-warning" role="status">تعذر التحقق من ملخص المبالغ؛ لم تُحسب أرصدة بديلة.</p>}
        {expanded && <div className="operations-card-expanded" id={contentId} data-testid={`operations-summary-details-${summary.visitId}`}>
            <section className="operations-summary-breakdown" aria-label="تفصيل إجمالي الزيارة">
                <h3>تفصيل الإجمالي</h3>
                <dl className="operations-metrics">
                    <Metric label="الخدمات" value={summary.serviceTotal} testId={`operations-summary-metric-${summary.visitId}-serviceTotal`} />
                    <Metric label="القطع" value={summary.partsTotal} testId={`operations-summary-metric-${summary.visitId}-partsTotal`} />
                    {summary.customerPurchaseTotal !== null && summary.customerPurchaseTotal > 0 && <Metric label="شراء ضمن الإجمالي" value={summary.customerPurchaseTotal} testId={`operations-summary-metric-${summary.visitId}-customerPurchaseTotal`} />}
                </dl>
            </section>
            <ItemRows summary={summary} items={ordinaryItems} />
            <SupplierRows summary={summary} items={suppliers} />
            <dl className="operations-provenance operations-summary-provenance">
                <Field label="التحمل">{summaryBearer(summary.items)}</Field>
                <Field label="مصدر الإدخال">{summary.origin}</Field>
                {summary.financialState === 'finalized' && <Field label="اعتمدها">{summary.finalizedBy}</Field>}
            </dl>
            <p className="operations-small-note">الملخص للحالة الحالية للزيارة، ويشمل بنودها كاملة.</p>
        </div>}
        <div className="operations-card-footer">
            <time dateTime={summary.updatedAt || undefined}>تحديث: {formatDate(summary.updatedAt)}</time>
            {expanded && <VehicleLink vehicleId={summary.vehicle.id} canOpenVehicle={canOpenVehicle} testId={`operations-log-view-vehicle-summary-${summary.visitId}`} />}
        </div>
        <ExpandButton expanded={expanded} onClick={() => setExpanded((value) => !value)} contentId={contentId} testId={`operations-summary-toggle-${summary.visitId}`} />
    </article>;
};

const EventFinancialDetails = ({ event }) => {
    if (event.eventType === 'customer_payment') {
        const visitLabel = event.visit.number ? `زيارة رقم ${event.visit.number}` : 'الزيارة المرتبطة';
        return <>
            <dl className="operations-event-facts">
                <Field label="الزيارة">{visitLabel}</Field>
                <Field label="الوسيلة">{methodLabel(event.payment.method)}</Field>
                {event.payment.reference && <Field label="مرجع الدفع"><bdi>{event.payment.reference}</bdi></Field>}
            </dl>
            <h3>توزيع الدفعة الحالي</h3>
            <dl className="operations-metrics operations-payment-allocation">
                <Metric label="تحصيل لصالح الورشة" value={event.payment.arApplied} testId={`operations-payment-applied-${event.id}`} />
                <Metric label="مقدم لصالح العميل" value={event.payment.unappliedAdvance} testId={`operations-payment-advance-${event.id}`} />
            </dl>
        </>;
    }
    if (['service_item', 'part_item', 'supplier_purchase'].includes(event.eventType)) return <dl className="operations-event-facts">
        <Field label="الكمية">{event.item.quantity === null ? 'غير متاح' : event.item.quantity}</Field>
        <Field label="سعر الوحدة"><Money value={event.item.unitPrice} /></Field>
        <Field label="التحمل">{bearerLabel(event.item)}</Field>
        {event.eventType === 'supplier_purchase' && <Field label="المورد">{event.item.supplierName || 'غير مسجل'}</Field>}
        {event.eventType === 'supplier_purchase' && event.item.supplierPaymentMethod && settlement(event.item.supplierSettlementStatus).settled && <Field label="وسيلة السداد">{methodLabel(event.item.supplierPaymentMethod)}</Field>}
        {event.eventType !== 'supplier_purchase' && <Field label="الأثر">{bearerKind(event.item) === 'customer' ? 'ضمن إجمالي العميل' : bearerKind(event.item) === 'workshop' ? 'قيمة بند خارج إجمالي العميل' : 'جهة التحمل غير محددة؛ لا يُفترض أثر مالي'}</Field>}
    </dl>;
    if (event.eventType === 'visit_finalized') return <p className="operations-small-note">مبلغ الاعتماد وقت التسجيل؛ الرصيد الحالي في ملخص الزيارة.</p>;
    return null;
};

export const OperationEventCard = ({ event, canOpenVehicle }) => {
    const [expanded, setExpanded] = useState(false);
    const presentation = eventPresentation(event);
    const operational = ['visit_received', 'vehicle_delivered'].includes(event.eventType);
    const contentId = `event-details-${event.id}`;
    return <article className={`operations-agreed-card operations-event-card operations-tone-${presentation.tone}`} data-testid={`operations-log-event-card-${event.id}`} data-card="event" data-tone={presentation.tone}>
        <div className="operations-card-top">
            <VehicleContext vehicle={event.vehicle} visit={event.visit} id={event.id} icon={presentation.icon} title={presentation.title} />
            {!operational && <div className="operations-card-main-total"><span>{event.eventType === 'visit_finalized' ? 'الإجمالي المعتمد' : 'المبلغ'}</span><Money value={event.amount} /></div>}
        </div>
        <div className="operations-event-subject"><strong>{presentation.subject}</strong><Badge>{presentation.cue}</Badge></div>
        {expanded && <div className="operations-card-expanded" id={contentId} data-testid={`operations-log-event-details-${event.id}`}>
            <EventFinancialDetails event={event} />
            <dl className="operations-provenance">
                <Field label="مصدر الإدخال">{event.origin}</Field>
                <Field label="سجّلها">{event.actor}</Field>
                <Field label="السجل">{event.source.label || SOURCE_LABELS[event.eventType] || 'غير مسجل'}</Field>
                {event.source.id && <Field label="مرجع السجل"><bdi className="operations-source-id">{event.source.id}</bdi></Field>}
            </dl>
        </div>}
        <div className="operations-card-footer">
            <time dateTime={event.occurredAt || undefined}>{formatDate(event.occurredAt)}</time>
            {expanded && <VehicleLink vehicleId={event.vehicle.id} canOpenVehicle={canOpenVehicle} testId={`operations-log-view-vehicle-event-${event.id}`} />}
        </div>
        <ExpandButton expanded={expanded} onClick={() => setExpanded((value) => !value)} contentId={contentId} testId={`operations-event-toggle-${event.id}`} />
    </article>;
};
